import { z } from 'zod';

import { evaluateHidden } from '@/components/form/SchemaForm/schemaFieldUtils';
import { buildFormSchema } from '@/utils/validation/formSchemaKit';
import {
  buildCommonValidationMessages,
  evaluateFieldRequired,
  isBlankFormString,
  type ValidatedField,
} from '@/utils/validation/schemaHelpers';

import {
  fieldDefToFieldConfig,
  fieldNameKey,
  isTabsFieldGroup,
  type ComposeSchemaFormOptions,
  type ComposedSchemaForm,
  type FieldDef,
  type FieldGroup,
  type FormGroup,
} from './types';

import type {
  SchemaSection,
  SectionConfig,
  TabsSectionConfig,
} from '@/components/form/SchemaForm/types';
import type { ValidationScope } from '@/utils/validation/jobProgressFields';
import type { TFunction } from 'i18next';

type FieldDefMeta = {
  def: FieldDef;
  key: string;
  /** True when the field (or its section/tabs group) is not shown. */
  isHidden: (values: Record<string, unknown>) => boolean;
};

function combineHiddenPredicates(
  ...predicates: Array<
    boolean | ((values: Record<string, unknown>) => boolean) | undefined
  >
): (values: Record<string, unknown>) => boolean {
  return (values) =>
    predicates.some((predicate) => evaluateHidden(predicate, values));
}

function flattenFieldDefsWithMeta(groups: FormGroup[]): FieldDefMeta[] {
  const metas: FieldDefMeta[] = [];

  for (const group of groups) {
    if (isTabsFieldGroup(group)) {
      for (const tab of group.tabs) {
        for (const tabGroup of tab.groups) {
          const sectionHidden = combineHiddenPredicates(
            group.hidden,
            tabGroup.hidden,
          );
          for (const def of tabGroup.fields) {
            metas.push({
              def,
              key: fieldNameKey(def.name),
              isHidden: combineHiddenPredicates(
                sectionHidden,
                def.ui.type === 'hidden' ? true : undefined,
                def.ui.hidden,
              ),
            });
          }
        }
      }
    } else {
      for (const def of group.fields) {
        metas.push({
          def,
          key: fieldNameKey(def.name),
          isHidden: combineHiddenPredicates(
            group.hidden,
            def.ui.type === 'hidden' ? true : undefined,
            def.ui.hidden,
          ),
        });
      }
    }
  }

  return metas;
}

function isEmptyRequiredValue(value: unknown): boolean {
  if (value == null || value === '') {
    return true;
  }
  if (Array.isArray(value) && value.length === 0) {
    return true;
  }
  if (typeof value === 'string' || typeof value === 'number') {
    return isBlankFormString(value);
  }
  return false;
}

function buildAutoRequiredRefine(
  fields: Record<string, ValidatedField>,
  scope: ValidationScope,
  requiredMessage: string,
) {
  return (schema: z.ZodObject<z.ZodRawShape>) =>
    schema.superRefine((data, ctx) => {
      for (const [key, field] of Object.entries(fields)) {
        if (!evaluateFieldRequired(field.isRequired, data, scope)) {
          continue;
        }
        const value = data[key];
        if (isEmptyRequiredValue(value)) {
          ctx.addIssue({
            code: 'custom',
            path: [key],
            message: requiredMessage,
          });
        }
      }
    });
}

/**
 * Field-level schemas always pass; real checks run in superRefine so hidden
 * fields can be skipped using full form values (siblings / section visibility).
 */
function buildHiddenAwareValidatedFields(
  metas: FieldDefMeta[],
  extraFields: Record<string, ValidatedField> | undefined,
): {
  validatedFields: Record<string, ValidatedField>;
  schemaMetas: FieldDefMeta[];
} {
  const validatedFields: Record<string, ValidatedField> = {
    ...extraFields,
  };
  const schemaMetas: FieldDefMeta[] = [];

  for (const meta of metas) {
    if (meta.key in validatedFields) {
      continue;
    }
    const original = meta.def.validation;
    schemaMetas.push(meta);
    validatedFields[meta.key] = {
      schema: z.unknown().optional(),
      isRequired: (values, scope, contractScope) => {
        if (meta.isHidden(values)) {
          return false;
        }
        return evaluateFieldRequired(
          original.isRequired,
          values,
          scope,
          contractScope,
        );
      },
    };
  }

  return { validatedFields, schemaMetas };
}

function buildFieldSchemaRefine(metas: FieldDefMeta[]) {
  return (data: Record<string, unknown>, ctx: z.RefinementCtx) => {
    for (const meta of metas) {
      if (meta.isHidden(data)) {
        continue;
      }
      const result = meta.def.validation.schema.safeParse(data[meta.key]);
      if (result.success) {
        continue;
      }
      for (const issue of result.error.issues) {
        ctx.addIssue({
          code: 'custom',
          path: [meta.key, ...issue.path],
          message: issue.message,
        });
      }
    }
  };
}

function omitHiddenFieldKeys(
  values: Record<string, unknown>,
  metas: FieldDefMeta[],
): Record<string, unknown> {
  const next: Record<string, unknown> = { ...values };
  for (const meta of metas) {
    if (meta.isHidden(values)) {
      delete next[meta.key];
    }
  }
  return next;
}

function fieldGroupToSection(group: FieldGroup): SectionConfig {
  return {
    key: group.key,
    title: group.title,
    description: group.description,
    divider: group.divider,
    hidden: group.hidden,
    fields: group.fields.map(fieldDefToFieldConfig),
  };
}

function tabsGroupToSection(
  group: Extract<FormGroup, { type: 'tabs' }>,
): TabsSectionConfig {
  return {
    type: 'tabs',
    key: group.key,
    hidden: group.hidden,
    activeKey: group.activeKey,
    onChange: group.onChange,
    tabs: group.tabs.map((tab) => ({
      key: tab.key,
      label: tab.label,
      sections: tab.groups.map(fieldGroupToSection),
    })),
  };
}

function groupsToSections(groups: FormGroup[]): SchemaSection[] {
  return groups.map((group) =>
    isTabsFieldGroup(group)
      ? tabsGroupToSection(group)
      : fieldGroupToSection(group),
  );
}

function collectGroupRefines(
  groups: FormGroup[],
  scope: ValidationScope,
): ((data: Record<string, unknown>, ctx: z.RefinementCtx) => void) | undefined {
  const refines: Array<
    (
      data: Record<string, unknown>,
      ctx: z.RefinementCtx,
      scope: ValidationScope,
    ) => void
  > = [];

  for (const group of groups) {
    if (isTabsFieldGroup(group)) {
      for (const tab of group.tabs) {
        for (const tabGroup of tab.groups) {
          if (tabGroup.refine) {
            refines.push(tabGroup.refine);
          }
        }
      }
    } else if (group.refine) {
      refines.push(group.refine);
    }
  }

  if (refines.length === 0) {
    return undefined;
  }

  return (data, ctx) => {
    refines.forEach((refine) => refine(data, ctx, scope));
  };
}

export function composeSchemaForm(
  groups: FormGroup[],
  options: ComposeSchemaFormOptions = {},
): ComposedSchemaForm {
  const scope = options.scope ?? 'save';
  const requiredMessage = options.requiredMessage ?? 'Required';
  const metas = flattenFieldDefsWithMeta(groups);
  const { validatedFields, schemaMetas } = buildHiddenAwareValidatedFields(
    metas,
    options.extraFields,
  );

  const groupRefine = collectGroupRefines(groups, scope);
  const autoRequiredRefine = buildAutoRequiredRefine(
    validatedFields,
    scope,
    requiredMessage,
  );
  const fieldSchemaRefine = buildFieldSchemaRefine(schemaMetas);

  const formSchema = buildFormSchema(validatedFields, (schema) => {
    let refined: z.ZodType = autoRequiredRefine(schema);
    refined = refined.superRefine((data, ctx) =>
      fieldSchemaRefine(data as Record<string, unknown>, ctx),
    );
    if (groupRefine) {
      refined = refined.superRefine((data, ctx) =>
        groupRefine(data as Record<string, unknown>, ctx),
      );
    }
    if (options.extraRefine) {
      refined = refined.superRefine((data, ctx) =>
        options.extraRefine?.(data as Record<string, unknown>, ctx, scope),
      );
    }
    return z.preprocess((raw) => {
      if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) {
        return raw;
      }
      return omitHiddenFieldKeys(raw as Record<string, unknown>, schemaMetas);
    }, refined);
  });

  return {
    formSchema,
    sections: groupsToSections(groups),
  };
}

export function composeSchemaFormWithT(
  t: TFunction,
  groups: FormGroup[],
  options: Omit<ComposeSchemaFormOptions, 'requiredMessage'> & {
    requiredMessage?: string;
  } = {},
): ComposedSchemaForm {
  const messages = buildCommonValidationMessages(t);
  return composeSchemaForm(groups, {
    ...options,
    requiredMessage: options.requiredMessage ?? messages.required,
  });
}
